import type { Ref, TextareaHTMLAttributes } from 'react';

import { maxDefaultLength } from '@/constants/numbers';
import { cn } from '@/lib/utils';

export type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  ref?: Ref<HTMLTextAreaElement>;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  invalid?: boolean;
  warning?: boolean;
  showResizer?: boolean;
};

const sizeClasses = {
  small: cn('min-h-11.5 rounded-sm px-3 text-sm'),
  medium: cn('min-h-13.5 rounded-md px-3 py-1 text-sm'),
  large: cn('min-h-16 rounded-md px-3 py-1.75 text-base'),
} satisfies Record<string, string>;

const TextArea = ({
  ref,
  className,
  size = 'large',
  warning,
  showResizer = false,
  disabled,
  maxLength = maxDefaultLength,
  ...props
}: TextAreaProps) => {
  const resizeStyle = showResizer && !disabled ? 'vertical' : 'none';

  return (
    <textarea
      ref={ref}
      disabled={disabled}
      data-warning={warning ? '' : undefined}
      maxLength={maxLength}
      className={cn(
        'w-full border border-gray-600 bg-white text-gray-900',
        'placeholder:text-gray-500',
        'hover:border-gray-900',
        'focus:border-green-500 focus:ring-1 focus:ring-green-500/50 focus:ring-offset-0 focus:outline-none',
        'disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-gray-100 disabled:text-gray-400',
        'aria-invalid:border-red-400 aria-invalid:hover:border-red-500 aria-invalid:focus:border-red-500 aria-invalid:focus:ring-red-500/50',
        'data-warning:border-yellow-400 data-warning:hover:border-yellow-500 data-warning:focus:border-yellow-500 data-warning:focus:ring-yellow-500/50',
        sizeClasses[size as keyof typeof sizeClasses],
        className,
      )}
      data-slot="textarea"
      style={{ resize: resizeStyle }}
      {...props}
    />
  );
};

TextArea.displayName = 'TextArea';

export default TextArea;
